/**
 * UGC video carousel in the product info column (snippets/ugc-videos.liquid):
 * - Tapping a cover plays its video in place, with sound; tapping again
 *   pauses it. Each video is added to the page (loading just its start) as
 *   its card nears the screen, and loads in full from the moment a finger
 *   touches the card, so it starts quickly.
 * - From the tap until the picture is actually moving, the play button
 *   shows a spinner and the cover stays in place; the same if it stalls.
 * - A corner button mutes and unmutes; the choice carries over to the next
 *   video played in the carousel.
 * - Playing one video pauses any other media on the page, and a video
 *   pauses when it's scrolled or swiped mostly out of view.
 * - The covers scroll natively (swipe on touch); with more than fit, arrows
 *   step through them and hide at either end.
 */
class UgcVideos extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ugc-track]');
    this.prevBtn = this.querySelector('[data-ugc-prev]');
    this.nextBtn = this.querySelector('[data-ugc-next]');

    this.boundHandleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.boundHandleClick);

    this.boundUpdateArrows = this.updateArrows.bind(this);
    this.track.addEventListener('scroll', this.boundUpdateArrows, { passive: true });
    this.resizeObserver = new ResizeObserver(this.boundUpdateArrows);
    this.resizeObserver.observe(this.track);

    // Pause a playing video once it's mostly out of view.
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (video && !video.paused && entry.intersectionRatio < 0.5) video.pause();
      });
    }, { threshold: [0, 0.5] });
    // Add each video as its card nears the screen.
    this.prepareObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.prepareObserver.unobserve(entry.target);
        UgcVideos.prepare(entry.target);
      });
    }, { rootMargin: '400px 0px' });

    this.querySelectorAll('[data-ugc-card]').forEach((card) => {
      this.visibilityObserver.observe(card);
      this.prepareObserver.observe(card);
    });

    // Load the whole video as soon as the card is touched, ahead of the click.
    this.boundHandlePointerDown = this.handlePointerDown.bind(this);
    this.addEventListener('pointerdown', this.boundHandlePointerDown, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
    this.removeEventListener('pointerdown', this.boundHandlePointerDown);
    this.track.removeEventListener('scroll', this.boundUpdateArrows);
    this.resizeObserver.disconnect();
    this.visibilityObserver.disconnect();
    this.prepareObserver.disconnect();
  }

  /**
   * @param {object} evt - Pointerdown event.
   */
  handlePointerDown(evt) {
    const toggle = evt.target.closest('[data-ugc-toggle]');
    if (!toggle) return;
    const video = UgcVideos.prepare(toggle.closest('[data-ugc-card]'));
    if (video) video.preload = 'auto';
  }

  /**
   * @param {object} evt - Click event.
   */
  handleClick(evt) {
    const muteBtn = evt.target.closest('[data-ugc-mute]');
    if (muteBtn) {
      this.setMuted(!this.muted);
      return;
    }

    const toggle = evt.target.closest('[data-ugc-toggle]');
    if (toggle) {
      this.toggle(toggle.closest('[data-ugc-card]'));
      return;
    }

    const arrow = evt.target.closest('[data-ugc-prev], [data-ugc-next]');
    if (arrow) {
      const step = this.track.clientWidth * 0.8 * (arrow.hasAttribute('data-ugc-next') ? 1 : -1);
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }
  }

  /**
   * Play a card's video, or pause it if playing.
   * @param {Element} card - The video's card.
   */
  toggle(card) {
    const video = UgcVideos.prepare(card);
    if (!video) return;

    if (!video.paused) {
      video.pause();
      return;
    }

    video.muted = Boolean(this.muted);
    video.preload = 'auto';
    card.classList.add('is-loading');
    UgcVideos.whenMoving(card, video, video.ended ? 0 : video.currentTime);

    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    const playing = video.play();
    if (playing) {
      // Blocked: back to the play button, tap again.
      playing.catch(() => card.classList.remove('is-loading'));
    }
  }

  /**
   * Add a card's video to the page from its template, if not done yet.
   * @param {Element} card - The video's card.
   * @returns {?HTMLVideoElement} The card's video.
   */
  static prepare(card) {
    let video = card.querySelector('video');
    if (video) return video;

    const template = card.querySelector('template[data-ugc-video]');
    if (!template) return null;
    card.prepend(template.content.cloneNode(true));
    video = card.querySelector('video');
    if (video) UgcVideos.bindVideo(card, video);
    return video;
  }

  /**
   * Once the video shows a frame past the given time, show it over the
   * cover and stop the spinner. ('playing' isn't enough: phones fire it
   * and then sit on the first frame while they buffer.)
   * @param {Element} card - The video's card.
   * @param {HTMLVideoElement} video - The card's video.
   * @param {number} fromTime - Playback position the picture must pass.
   */
  static whenMoving(card, video, fromTime) {
    const done = () => {
      card.classList.add('has-started');
      card.classList.remove('is-loading');
    };

    if ('requestVideoFrameCallback' in video) {
      const check = (now, frame) => {
        if (frame.mediaTime > fromTime) {
          done();
        } else {
          video.requestVideoFrameCallback(check);
        }
      };
      video.requestVideoFrameCallback(check);
      return;
    }

    const onTimeUpdate = () => {
      if (video.currentTime <= fromTime) return;
      video.removeEventListener('timeupdate', onTimeUpdate);
      done();
    };
    video.addEventListener('timeupdate', onTimeUpdate);
  }

  /**
   * Keep a card's state (play button or spinner, button label) in step with
   * its video.
   * @param {Element} card - The video's card.
   * @param {HTMLVideoElement} video - The card's video.
   */
  static bindVideo(card, video) {
    const toggle = card.querySelector('[data-ugc-toggle]');
    const setPlaying = (playing) => {
      card.classList.toggle('is-playing', playing);
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      if (!playing) card.classList.remove('is-loading');
    };
    video.addEventListener('play', () => setPlaying(true));
    video.addEventListener('pause', () => setPlaying(false));
    video.addEventListener('ended', () => setPlaying(false));

    // Stalled mid-play: spinner until it moves again.
    video.addEventListener('waiting', () => {
      if (video.paused) return;
      card.classList.add('is-loading');
      UgcVideos.whenMoving(card, video, video.currentTime);
    });
  }

  /**
   * Mute or unmute the carousel's videos, updating every mute button.
   * @param {boolean} muted - Whether to mute.
   */
  setMuted(muted) {
    this.muted = muted;
    this.querySelectorAll('video').forEach((video) => {
      video.muted = muted;
    });
    this.querySelectorAll('[data-ugc-mute]').forEach((btn) => {
      btn.classList.toggle('is-muted', muted);
      btn.setAttribute('aria-label', muted ? btn.dataset.labelUnmute : btn.dataset.labelMute);
    });
  }

  /**
   * Show the arrows only when the covers overflow, hiding the one that can't
   * go further.
   */
  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const scrolled = Math.abs(this.track.scrollLeft);
    this.prevBtn.hidden = maxScroll <= 1 || scrolled <= 1;
    this.nextBtn.hidden = maxScroll <= 1 || scrolled >= maxScroll - 1;
  }
}

if (!customElements.get('ugc-videos')) {
  customElements.define('ugc-videos', UgcVideos);
}
